import React from 'react';
import UserContextSelectContainer from 'components/triggers/UserContextSelectContainer';
import TriggerIconButton from 'components/triggers/TriggerIconButton';
import './UserContextView.styl';
import classnames from 'classnames';
import {UserConditionType} from 'records/UserCondition';
import {UserContextType} from 'components/triggers/UserContextType';
import {CircleXIcon} from '@components/icon/CircleXIcon';
import {CogIcon} from '@components/icon/CogIcon';

class UserContextViewRow extends React.Component {
  constructor(props) {
    super(props);

    this.toggleSelection = this.toggleSelection.bind(this);
    this.closeUserSelection = this.closeUserSelection.bind(this);
    this.saveUserSelection = this.saveUserSelection.bind(this);
    this.updateUserSelection = this.updateUserSelection.bind(this);

    this.state = {
      userSelectionVisible: false,
      previousContext: null,
    };
  }

  toggleSelection() {
    this.props.toggleSelection && this.props.toggleSelection(!this.state.userSelectionVisible);
    this.setState({
      userSelectionVisible: !this.state.userSelectionVisible,
    });
  }

  saveUserSelection(context) {
    this.props.updateUserContext(context);
    this.setState({
      userSelectionVisible: false,
      previousContext: context,
    });
    this.props.toggleSelection && this.props.toggleSelection(false);
  }

  updateUserSelection(context) {
    this.props.updateUserContext(context);
  }

  closeUserSelection(reason) {
    if (this.state.previousContext) {
      this.props.updateUserContext(this.state.previousContext);
    }
    this.setState({
      userSelectionVisible: false,
    });
    this.props.toggleSelection && this.props.toggleSelection(false);
  }

  renderDescription(context, contextType, defaultText) {
    const component = context.component;
    if (component !== undefined) {
      return (
        <context.component
          context={context}
          contextType={contextType}
          onClick={this.toggleSelection}
        />
      );
    }
    return <span>{defaultText}</span>;
  }

  componentDidMount() {
    if (this.props.context) {
      this.setState({
        previousContext: this.props.context,
      });
    }
  }

  render() {
    const {context, contextType, defaultText, label} = this.props;
    const selectedValue = context && context.getValue && context.getValue();
    return (
      <div className="context-item">
        {label && <div>{label}&nbsp;</div>}
        {this.renderDescription(context, contextType, defaultText)}
        {this.state.userSelectionVisible && (
          <div className="create-rule-popout">
            <UserContextSelectContainer
              saveSelection={this.saveUserSelection}
              updateSelection={this.updateUserSelection}
              closeOnSelect={false}
              closeRequest={this.closeUserSelection}
              selectedContextType={context.type}
              selectedValue={selectedValue}
              contextType={contextType}
            />
          </div>
        )}
      </div>
    );
  }
}

class UserContextViewPlusButton extends React.Component {
  constructor(props) {
    super(props);

    this.toggleSelection = this.toggleSelection.bind(this);
    this.saveSelection = this.saveSelection.bind(this);
    this.updateSelection = this.updateSelection.bind(this);
    this.renderDescription = this.renderDescription.bind(this);

    this.state = {
      selectionVisible: false,
      newContext: null,
    };
  }

  toggleSelection() {
    this.props.onSelect && this.props.onSelect(!this.state.selectionVisible);
    const newContext = !this.state.selectionVisible ? {} : null;
    this.setState({
      selectionVisible: !this.state.selectionVisible,
      newContext,
    });
  }

  saveSelection(context) {
    this.props.onSelect && this.props.onSelect(false);
    this.setState({
      selectionVisible: false,
      newContext: null,
    });
    this.props.addItem(context);
  }

  updateSelection(context) {
    this.setState({newContext: context});
  }

  renderDescription() {
    const {selectionVisible, newContext} = this.state;
    const {buttonLabel, contextPathMap, contextType, cog = false} = this.props;
    return newContext == null && (!contextPathMap || contextPathMap.size === 0) ? (
      <TriggerIconButton
        label={buttonLabel}
        onClick={this.toggleSelection}
        active={selectionVisible}
        icon={cog ? <CogIcon tooltip="Edit" /> : null}
      />
    ) : (
      <UserContextView
        {...this.props}
        orClick={this.toggleSelection}
        defaultText={buttonLabel}
        contextType={contextType}
        newContext={newContext}
        toggleSelection={this.props.onSelect}
      />
    );
  }

  render() {
    const {selectionVisible} = this.state;
    return (
      <div className={classnames('user-context-view', selectionVisible && 'active')}>
        {this.renderDescription()}
        {selectionVisible && (
          <div className="create-rule-popout">
            <UserContextSelectContainer
              saveSelection={this.saveSelection}
              updateSelection={this.updateSelection}
              closeOnSelect={false}
              closeRequest={this.toggleSelection}
              contextType={this.props.contextType}
            />
          </div>
        )}
      </div>
    );
  }
}

export default class UserContextView extends React.Component {
  constructor(props) {
    super(props);

    this.updateUserContext = this.updateUserContext.bind(this);
  }

  updateUserContext(path, context) {
    if (path !== null) {
      this.props.updateItem(path, context);
    }
  }

  hasAnyUser(userContext) {
    const anyUserCondition = userContext.find(it => it.type === UserConditionType.ANY_USER);
    return !!anyUserCondition;
  }

  /**
   * defines the prefix (conjunction) that should be in front of a user condition
   * @param isFirst is this the first condition
   * @param context the condition
   * @param contextType which variation of the view/edit
   * @returns {string}
   */
  prefixLabel(isFirst, context, contextType) {
    if (isFirst && contextType !== UserContextType.CALL) {
      return '';
    } else if (isFirst) {
      return 'and';
    } else {
      return 'or';
    }
  }

  render() {
    const {
      contextPathMap, // list of path/context pairs
      contextType,
      defaultText,
      newContext,
    } = this.props;
    const allContexts =
      newContext === null ? contextPathMap : contextPathMap.push([null, newContext]);

    const canAddMore = !this.hasAnyUser(allContexts.map(pathAndContext => pathAndContext[1]));
    const lastIndex = allContexts.size - 1;
    return (
      <div className="rule-option-list">
        <ul>
          {allContexts.map((pathAndContext, i) => (
            <li key={i}>
              <UserContextViewRow
                context={pathAndContext[1]}
                label={this.prefixLabel(i === 0, pathAndContext[1], contextType)}
                contextType={contextType}
                defaultText={defaultText}
                updateUserContext={updatedContext =>
                  this.updateUserContext(pathAndContext[0], updatedContext)
                }
                toggleSelection={this.props.toggleSelection}
              />
              {i === lastIndex && canAddMore && (
                <div className="or-button" onClick={this.props.orClick}>
                  or
                </div>
              )}
              <div className="delete" onClick={() => this.props.removeItem(pathAndContext[0])}>
                <CircleXIcon tooltip="Remove" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

export {UserContextViewPlusButton};
